import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadToFirebaseStorage, PRESET_LOGO } from '../../utils/firebaseStorage';
import {
    getDeliveryCoverageLabel,
    getDeliveryCoverageLocationOptions,
    getDeliveryCoverageTypeOptions,
    normalizeCoverageMode,
    normalizeDeliveryCoverageType,
} from '../../utils/metroManilaLocations';
import '../AdminSettings/AdminSettings.css';

const defaultStoreInfo = {
    storeName: '',
    emailAddress: '',
    storeAddress: '',
    contactNumber: '',
    businessHours: '',
    logoUrl: '',
};

const defaultGcash = {
    gcashAccountName: '',
    gcashNumber: '',
    paymentInstructions: '',
    gcashQrUrl: '',
};

const defaultDeliveryConfig = {
    maxItemsPerOrder: 50,
    fourWheelItemThreshold: 50,
};

const COURIER_OPTIONS = ['Lalamove', 'LBC', 'J&T', 'JG Courier'];

const EditIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const UploadIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="17 8 12 3 7 8"/>
        <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
);
const RemoveIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);
const FileIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="12" y1="18" x2="12" y2="12"/>
        <line x1="9" y1="15" x2="15" y2="15"/>
    </svg>
);
const PlusIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);

function Toggle({ checked, onChange }) {
    return (
        <label className="ss-toggle">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="ss-toggle-track">
                <span className="ss-toggle-thumb" />
            </span>
        </label>
    );
}

function uploadPreview(url, fallback = 'Choose a file or drag and drop it here') {
    if (url) return <img src={url} alt="Preview" />;
    return fallback;
}

function createPaymentMethod() {
    return {
        id: `pm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name: 'Bank Transfer',
        bankName: '',
        accountName: '',
        accountNumber: '',
        instructions: '',
        qrUrl: '',
        enabled: true,
    };
}

function createDeliveryMethod(vehicleType = 'motorcycle') {
    return {
        id: `dm_${vehicleType}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name: vehicleType === 'four_wheel' ? '4-Wheel Vehicle Delivery' : 'Motorcycle Delivery',
        vehicleType,
        courierName: vehicleType === 'four_wheel' ? 'Lalamove' : 'JG Courier',
        fee: '',
        estimatedTime: '',
        coverageType: vehicleType === 'pickup' ? 'all' : 'city',
        coverageValue: vehicleType === 'pickup' ? 'All areas' : 'Taguig City, Metro Manila',
        enabled: true,
    };
}

function getLegacyCoverageDefaults(coverageMode) {
    const normalized = normalizeCoverageMode(coverageMode);
    if (normalized === 'taguig-only') {
        return {
            coverageType: 'city',
            coverageValue: 'Taguig City, Metro Manila',
        };
    }
    return {
        coverageType: 'region',
        coverageValue: 'Metro Manila',
    };
}

function sanitizePaymentMethods(methods = []) {
    return methods
        .filter((method) => {
            const lowerName = String(method?.name || '').toLowerCase();
            return !(lowerName.includes('cash on delivery') || lowerName === 'cod');
        })
        .map((method) => ({
            id: method.id || `pm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            name: method.name || '',
            bankName: method.bankName || '',
            accountName: method.accountName || '',
            accountNumber: method.accountNumber || '',
            instructions: method.instructions || '',
            qrUrl: method.qrUrl || '',
            enabled: method.enabled !== false,
        }));
}

function ensureDeliveryMethods(methods = [], legacyCoverageMode = 'metro-manila') {
    const legacyDefaults = getLegacyCoverageDefaults(legacyCoverageMode);
    const normalized = methods.map((method) => ({
        id: method.id || `dm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name: method.name || '',
        vehicleType: method.vehicleType || '',
        courierName: method.courierName || '',
        fee: method.fee || '',
        estimatedTime: method.estimatedTime || '',
        coverageType: normalizeDeliveryCoverageType(method.coverageType || (method.vehicleType === 'pickup' ? 'all' : legacyDefaults.coverageType)),
        coverageValue: method.coverageValue || method.coverageArea || (method.vehicleType === 'pickup' ? 'All areas' : legacyDefaults.coverageValue),
        enabled: method.enabled !== false,
    }));

    if (!normalized.some((method) => method.vehicleType === 'motorcycle')) {
        normalized.push(createDeliveryMethod('motorcycle'));
    }
    if (!normalized.some((method) => method.vehicleType === 'four_wheel')) {
        normalized.push(createDeliveryMethod('four_wheel'));
    }

    return normalized;
}

function MethodUpload({ label, previewUrl, onFileChange, onRemove }) {
    return (
        <div className="settings-upload-box-wrap">
            <label className="settings-section-label">{label}</label>
            <div className="settings-upload-box settings-upload-box--small">
                {uploadPreview(previewUrl, (
                    <>
                        <FileIcon />
                        <span>Upload a QR image</span>
                    </>
                ))}
                <input type="file" accept="image/*" onChange={onFileChange} />
            </div>
            <div className="settings-upload-btns">
                <label className="settings-btn-upload">
                    <UploadIcon />
                    Upload
                    <input type="file" accept="image/*" onChange={onFileChange} />
                </label>
                <button className="settings-btn-remove" onClick={onRemove} type="button">
                    <RemoveIcon />
                    Remove
                </button>
            </div>
        </div>
    );
}

function PaymentMethodCard({ method, onChange, onDelete }) {
    const preview = method.qrPreviewUrl || method.qrUrl || '';

    return (
        <div className={`ss-method-card ${method.enabled ? '' : 'ss-method-card--disabled'}`}>
            <div className="ss-method-card-header">
                <div className="ss-method-card-left">
                    <span className="ss-method-card-name">{method.name || 'Unnamed Payment Method'}</span>
                </div>
                <div className="ss-method-card-right">
                    <Toggle checked={method.enabled} onChange={() => onChange({ ...method, enabled: !method.enabled })} />
                    <button className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(method.id)} type="button" aria-label="Delete payment method">
                        <TrashIcon />
                    </button>
                </div>
            </div>
            <div className="ss-method-card-body">
                <div className="settings-upload-area">
                    <MethodUpload
                        label="Transfer QR Code"
                        previewUrl={preview}
                        onFileChange={(event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            onChange({
                                ...method,
                                qrFile: file,
                                qrPreviewUrl: URL.createObjectURL(file),
                            });
                        }}
                        onRemove={() => onChange({ ...method, qrFile: null, qrPreviewUrl: '', qrUrl: '' })}
                    />
                    <div className="settings-gcash-fields">
                        <div className="settings-form-grid">
                            <div className="settings-field">
                                <label>Payment Method Name</label>
                                <input type="text" value={method.name} onChange={(e) => onChange({ ...method, name: e.target.value })} />
                            </div>
                            <div className="settings-field">
                                <label>Bank Name</label>
                                <input type="text" value={method.bankName} onChange={(e) => onChange({ ...method, bankName: e.target.value })} />
                            </div>
                            <div className="settings-field">
                                <label>Account Name</label>
                                <input type="text" value={method.accountName} onChange={(e) => onChange({ ...method, accountName: e.target.value })} />
                            </div>
                            <div className="settings-field">
                                <label>Account Number</label>
                                <input type="text" value={method.accountNumber} onChange={(e) => onChange({ ...method, accountNumber: e.target.value })} />
                            </div>
                        </div>
                        <div className="settings-field">
                            <label>Payment Instructions</label>
                            <textarea rows={3} value={method.instructions} onChange={(e) => onChange({ ...method, instructions: e.target.value })} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DeliveryMethodCard({ method, onChange, onDelete, coverageTypeOptions, coverageOptionsByType }) {
    const normalizedCoverageType = normalizeDeliveryCoverageType(method.coverageType);
    const coverageOptions = coverageOptionsByType[normalizedCoverageType] || [];
    const datalistId = `delivery-coverage-${method.id}`;
    const displayName = method.courierName ? `${method.name || 'Unnamed Delivery Method'} (${method.courierName})` : (method.name || 'Unnamed Delivery Method');

    return (
        <div className={`ss-method-card ${method.enabled ? '' : 'ss-method-card--disabled'}`}>
            <div className="ss-method-card-header">
                <div className="ss-method-card-left">
                    <span className="ss-method-card-name">{displayName}</span>
                </div>
                <div className="ss-method-card-right">
                    <Toggle checked={method.enabled} onChange={() => onChange({ ...method, enabled: !method.enabled })} />
                    <button className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(method.id)} type="button" aria-label="Delete delivery method">
                        <TrashIcon />
                    </button>
                </div>
            </div>
            <div className="ss-method-card-body">
                <div className="settings-form-grid">
                    <div className="settings-field">
                        <label>Delivery Method Name</label>
                        <input type="text" value={method.name} onChange={(e) => onChange({ ...method, name: e.target.value })} />
                    </div>
                    <div className="settings-field">
                        <label>Vehicle Type</label>
                        <select value={method.vehicleType} onChange={(e) => onChange({ ...method, vehicleType: e.target.value })}>
                            <option value="motorcycle">Motorcycle</option>
                            <option value="four_wheel">4-Wheel Vehicle</option>
                        </select>
                    </div>
                    <div className="settings-field">
                        <label>Courier</label>
                        <select value={method.courierName || ''} onChange={(e) => onChange({ ...method, courierName: e.target.value })}>
                            <option value="">Select courier</option>
                            {COURIER_OPTIONS.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                    <div className="settings-field">
                        <label>Delivery Fee</label>
                        <input type="text" value={method.fee} onChange={(e) => onChange({ ...method, fee: e.target.value })} placeholder="e.g. 150" />
                    </div>
                    <div className="settings-field">
                        <label>Estimated Delivery Time</label>
                        <input type="text" value={method.estimatedTime} onChange={(e) => onChange({ ...method, estimatedTime: e.target.value })} placeholder="e.g. Same day" />
                    </div>
                    <div className="settings-field">
                        <label>Delivery Coverage Type</label>
                        <select
                            value={normalizedCoverageType}
                            onChange={(e) => onChange({
                                ...method,
                                coverageType: e.target.value,
                                coverageValue: e.target.value === 'all' ? 'All areas' : '',
                            })}
                        >
                            {coverageTypeOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="settings-field">
                        <label>Coverage Area</label>
                        <input
                            type="text"
                            list={datalistId}
                            value={normalizedCoverageType === 'all' ? 'All areas' : method.coverageValue}
                            onChange={(e) => onChange({ ...method, coverageValue: e.target.value })}
                            placeholder={normalizedCoverageType === 'barangay' ? 'Type a barangay' : 'Type to search locations'}
                            disabled={normalizedCoverageType === 'all'}
                        />
                        <datalist id={datalistId}>
                            {coverageOptions.map((option) => (
                                <option key={`${method.id}-${option.type}-${option.value}`} value={option.value}>{option.label}</option>
                            ))}
                        </datalist>
                        <span className="ss-field-hint">
                            {normalizedCoverageType === 'all'
                                ? 'This method will be available for all addresses.'
                                : normalizedCoverageType === 'island_group'
                                    ? 'Select Luzon, Visayas, or Mindanao to allow deliveries anywhere within that island group.'
                                : `Search and select a ${normalizedCoverageType === 'city' ? 'city or municipality' : normalizedCoverageType} from the same address directory used in customer saved addresses.`}
                        </span>
                    </div>
                    <div className="settings-field settings-form-grid--full">
                        <label>Coverage Preview</label>
                        <div className="ss-coverage-preview">
                            {getDeliveryCoverageLabel(normalizedCoverageType, method.coverageValue)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function StoreSettings() {
    const [storeInfo, setStoreInfo] = useState(defaultStoreInfo);
    const [storeInfoDraft, setStoreInfoDraft] = useState(defaultStoreInfo);
    const [storeInfoEditing, setStoreInfoEditing] = useState(false);
    const [logoPreview, setLogoPreview] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [storeInfoMsg, setStoreInfoMsg] = useState(null);
    const [savingStoreInfo, setSavingStoreInfo] = useState(false);

    const [payment, setPayment] = useState(defaultGcash);
    const [paymentDraft, setPaymentDraft] = useState(defaultGcash);
    const [paymentEditing, setPaymentEditing] = useState(false);
    const [qrPreview, setQrPreview] = useState('');
    const [qrFile, setQrFile] = useState(null);
    const [paymentMsg, setPaymentMsg] = useState(null);

    const [deliveryConfig, setDeliveryConfig] = useState(defaultDeliveryConfig);
    const [deliveryConfigDraft, setDeliveryConfigDraft] = useState(defaultDeliveryConfig);
    const [deliveryConfigDirty, setDeliveryConfigDirty] = useState(false);
    const [deliveryConfigMsg, setDeliveryConfigMsg] = useState(null);

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentMethodsDirty, setPaymentMethodsDirty] = useState(false);
    const [paymentMethodsMsg, setPaymentMethodsMsg] = useState(null);
    const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);

    const [deliveryMethods, setDeliveryMethods] = useState([]);
    const [deliveryMethodsDirty, setDeliveryMethodsDirty] = useState(false);
    const [deliveryMethodsMsg, setDeliveryMethodsMsg] = useState(null);
    const [coverageOptionsByType, setCoverageOptionsByType] = useState({
        all: [{ value: 'All areas', label: 'All areas' }],
        island_group: [],
        region: [],
        province: [],
        city: [],
        barangay: [],
    });

    const coverageTypeOptions = useMemo(() => getDeliveryCoverageTypeOptions(), []);

    useEffect(() => {
        loadSettings();
    }, []);

    useEffect(() => {
        let isMounted = true;
        Promise.all([
            getDeliveryCoverageLocationOptions('all'),
            getDeliveryCoverageLocationOptions('island_group'),
            getDeliveryCoverageLocationOptions('region'),
            getDeliveryCoverageLocationOptions('province'),
            getDeliveryCoverageLocationOptions('city'),
            getDeliveryCoverageLocationOptions('barangay'),
        ]).then(([all, island_group, region, province, city, barangay]) => {
            if (!isMounted) return;
            setCoverageOptionsByType({ all, island_group, region, province, city, barangay });
        }).catch((error) => {
            console.error('Failed to load delivery coverage options:', error);
        });
        return () => { isMounted = false; };
    }, []);

    async function loadSettings() {
        try {
            const snap = await getDoc(doc(db, 'settings', 'store'));
            if (!snap.exists()) {
                setPaymentMethods([createPaymentMethod()]);
                setDeliveryMethods(ensureDeliveryMethods([]));
                return;
            }

            const data = snap.data() || {};
            const nextStoreInfo = {
                storeName: data.storeName || '',
                emailAddress: data.emailAddress || '',
                storeAddress: data.storeAddress || '',
                contactNumber: data.contactNumber || '',
                businessHours: data.businessHours || '',
                logoUrl: data.logoUrl || '',
            };
            const nextPayment = {
                gcashAccountName: data.gcashAccountName || '',
                gcashNumber: data.gcashNumber || '',
                paymentInstructions: data.paymentInstructions || '',
                gcashQrUrl: data.gcashQrUrl || '',
            };
            const nextDeliveryConfig = {
                maxItemsPerOrder: Number(data.deliveryConfig?.maxItemsPerOrder || data.orderLimits?.maxItemsPerOrder || 50) || 50,
                fourWheelItemThreshold: Number(data.deliveryConfig?.fourWheelItemThreshold || data.orderLimits?.fourWheelItemThreshold || 50) || 50,
            };
            const legacyCoverageMode = normalizeCoverageMode(data.deliveryConfig?.coverageMode);

            setStoreInfo(nextStoreInfo);
            setStoreInfoDraft(nextStoreInfo);
            setLogoPreview(nextStoreInfo.logoUrl);
            setPayment(nextPayment);
            setPaymentDraft(nextPayment);
            setQrPreview(nextPayment.gcashQrUrl);
            setDeliveryConfig(nextDeliveryConfig);
            setDeliveryConfigDraft(nextDeliveryConfig);
            setPaymentMethods(sanitizePaymentMethods(data.paymentMethods || []));
            setDeliveryMethods(ensureDeliveryMethods(data.deliveryMethods || [], legacyCoverageMode));
        } catch (error) {
            console.error('Error loading store settings:', error);
        }
    }

    async function saveStoreInfo() {
        setSavingStoreInfo(true);
        try {
            let logoUrl = storeInfoDraft.logoUrl || '';
            if (logoFile) {
                const upload = await uploadToFirebaseStorage(logoFile, 'jg-printing/store', PRESET_LOGO);
                if (upload.error) {
                    setStoreInfoMsg({ type: 'error', text: `Logo upload failed: ${upload.error}` });
                    return;
                }
                logoUrl = upload.url || '';
            }

            const updated = { ...storeInfoDraft, logoUrl };
            await setDoc(doc(db, 'settings', 'store'), updated, { merge: true });
            setStoreInfo(updated);
            setStoreInfoDraft(updated);
            setLogoPreview(logoUrl);
            setLogoFile(null);
            setStoreInfoEditing(false);
            setStoreInfoMsg({ type: 'success', text: 'Store information saved successfully.' });
        } catch (error) {
            setStoreInfoMsg({ type: 'error', text: 'Failed to save store information.' });
        } finally {
            setSavingStoreInfo(false);
        }
    }

    async function saveGcashDetails() {
        try {
            let gcashQrUrl = paymentDraft.gcashQrUrl || '';
            if (qrFile) {
                const upload = await uploadToFirebaseStorage(qrFile, 'jg-printing/payments');
                if (upload.error) {
                    setPaymentMsg({ type: 'error', text: `GCash QR upload failed: ${upload.error}` });
                    return;
                }
                gcashQrUrl = upload.url || '';
            }

            const updated = { ...paymentDraft, gcashQrUrl };
            await setDoc(doc(db, 'settings', 'store'), updated, { merge: true });
            setPayment(updated);
            setPaymentDraft(updated);
            setQrPreview(gcashQrUrl);
            setQrFile(null);
            setPaymentEditing(false);
            setPaymentMsg({ type: 'success', text: 'GCash details saved successfully.' });
        } catch (error) {
            setPaymentMsg({ type: 'error', text: 'Failed to save GCash details.' });
        }
    }

    async function saveDeliveryConfig() {
        try {
            const normalized = {
                maxItemsPerOrder: Math.max(1, Number(deliveryConfigDraft.maxItemsPerOrder || 1)),
                fourWheelItemThreshold: Math.max(1, Number(deliveryConfigDraft.fourWheelItemThreshold || 1)),
            };
            await setDoc(doc(db, 'settings', 'store'), { deliveryConfig: normalized, orderLimits: normalized }, { merge: true });
            setDeliveryConfig(normalized);
            setDeliveryConfigDraft(normalized);
            setDeliveryConfigDirty(false);
            setDeliveryConfigMsg({ type: 'success', text: 'Delivery rules saved successfully.' });
        } catch (error) {
            setDeliveryConfigMsg({ type: 'error', text: 'Failed to save delivery rules.' });
        }
    }

    async function savePaymentMethods() {
        setSavingPaymentMethods(true);
        try {
            // Upload all QR codes in parallel
            const uploaded = await Promise.all(
                paymentMethods.map(async (method) => {
                    let qrUrl = method.qrUrl || '';
                    if (method.qrFile) {
                        const upload = await uploadToFirebaseStorage(method.qrFile, 'jg-printing/payments', PRESET_LOGO);
                        if (upload.error) throw new Error(`QR upload failed for ${method.name || 'a payment method'}: ${upload.error}`);
                        qrUrl = upload.url || '';
                    }
                    return {
                        id: method.id,
                        name: method.name,
                        bankName: method.bankName,
                        accountName: method.accountName,
                        accountNumber: method.accountNumber,
                        instructions: method.instructions,
                        qrUrl,
                        enabled: method.enabled !== false,
                    };
                })
            );

            await setDoc(doc(db, 'settings', 'store'), { paymentMethods: sanitizePaymentMethods(uploaded) }, { merge: true });
            setPaymentMethods(sanitizePaymentMethods(uploaded));
            setPaymentMethodsDirty(false);
            setPaymentMethodsMsg({ type: 'success', text: 'Payment methods saved successfully.' });
        } catch (error) {
            setPaymentMethodsMsg({ type: 'error', text: error.message || 'Failed to save payment methods.' });
        } finally {
            setSavingPaymentMethods(false);
        }
    }

    async function saveDeliveryMethods() {
        try {
            const sanitized = ensureDeliveryMethods(deliveryMethods).map((method) => ({
                id: method.id,
                name: method.name,
                vehicleType: method.vehicleType,
                courierName: method.courierName || '',
                fee: method.fee,
                estimatedTime: method.estimatedTime,
                coverageType: normalizeDeliveryCoverageType(method.coverageType),
                coverageValue: normalizeDeliveryCoverageType(method.coverageType) === 'all'
                    ? 'All areas'
                    : method.coverageValue,
                coverageLabel: getDeliveryCoverageLabel(method.coverageType, method.coverageValue),
                enabled: method.enabled !== false,
            }));
            await setDoc(doc(db, 'settings', 'store'), { deliveryMethods: sanitized }, { merge: true });
            setDeliveryMethods(sanitized);
            setDeliveryMethodsDirty(false);
            setDeliveryMethodsMsg({ type: 'success', text: 'Delivery methods saved successfully.' });
        } catch (error) {
            setDeliveryMethodsMsg({ type: 'error', text: 'Failed to save delivery methods.' });
        }
    }

    return (
        <div>
            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Store Information</h2>
                    <div className="settings-card-actions">
                        {!storeInfoEditing ? (
                            <button className="settings-btn-edit" onClick={() => { setStoreInfoDraft(storeInfo); setStoreInfoEditing(true); }}>
                                <EditIcon />
                                Edit
                            </button>
                        ) : (
                            <>
                                <button className="settings-btn-cancel" onClick={() => { setStoreInfoDraft(storeInfo); setStoreInfoEditing(false); setLogoPreview(storeInfo.logoUrl || ''); setLogoFile(null); }} disabled={savingStoreInfo}>Cancel</button>
                                <button className="settings-btn-save" onClick={saveStoreInfo} disabled={savingStoreInfo}>{savingStoreInfo ? 'Saving…' : 'Save Changes'}</button>
                            </>
                        )}
                    </div>
                </div>
                {storeInfoMsg && <div className={`settings-msg settings-msg--${storeInfoMsg.type}`}>{storeInfoMsg.text}</div>}
                <div className="settings-upload-area">
                    <div className="settings-upload-box-wrap">
                        <label className="settings-section-label">Store Logo</label>
                        <div className="settings-upload-box">
                            {uploadPreview(logoPreview, (
                                <>
                                    <FileIcon />
                                    <span>Choose a file or drag and drop it here</span>
                                </>
                            ))}
                            {storeInfoEditing && (
                                <input type="file" accept="image/*" onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (!file) return;
                                    setLogoFile(file);
                                    setLogoPreview(URL.createObjectURL(file));
                                }} />
                            )}
                        </div>
                    </div>
                    <div className="settings-gcash-fields">
                        <div className="settings-form-grid">
                            <div className="settings-field">
                                <label>Store Name</label>
                                <input value={storeInfoDraft.storeName} disabled={!storeInfoEditing} onChange={(e) => setStoreInfoDraft({ ...storeInfoDraft, storeName: e.target.value })} />
                            </div>
                            <div className="settings-field">
                                <label>Email Address</label>
                                <input value={storeInfoDraft.emailAddress} disabled={!storeInfoEditing} onChange={(e) => setStoreInfoDraft({ ...storeInfoDraft, emailAddress: e.target.value })} />
                            </div>
                            <div className="settings-field">
                                <label>Store Address</label>
                                <input value={storeInfoDraft.storeAddress} disabled={!storeInfoEditing} onChange={(e) => setStoreInfoDraft({ ...storeInfoDraft, storeAddress: e.target.value })} />
                            </div>
                            <div className="settings-field">
                                <label>Contact Number</label>
                                <input value={storeInfoDraft.contactNumber} disabled={!storeInfoEditing} onChange={(e) => setStoreInfoDraft({ ...storeInfoDraft, contactNumber: e.target.value })} />
                            </div>
                            <div className="settings-field settings-form-grid--full">
                                <label>Business Hours</label>
                                <input value={storeInfoDraft.businessHours} disabled={!storeInfoEditing} onChange={(e) => setStoreInfoDraft({ ...storeInfoDraft, businessHours: e.target.value })} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Online Payments (PayMongo)</h2>
                </div>
                <div className="settings-gcash-fields">
                    <p className="settings-help-text">
                        All online payments (GCash, Maya, cards, bank e-wallets) are processed exclusively through PayMongo.
                        Direct GCash QR and manual bank transfer options have been removed from checkout.
                    </p>
                    <p className="settings-help-text">
                        Configure Firebase Function secrets before deploy:
                        {' '}<code>PAYMONGO_SECRET_KEY</code>, <code>PAYMONGO_WEBHOOK_SECRET</code>, <code>PAYMENT_HMAC_SECRET</code>.
                        See <code>functions/.env.example</code> and <code>PAYMENT_SYSTEM.md</code>.
                    </p>
                </div>
            </div>

            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Delivery Rules</h2>
                    <div className="settings-card-actions">
                        {deliveryConfigDirty && (
                            <>
                                <button className="settings-btn-cancel" onClick={() => { setDeliveryConfigDraft(deliveryConfig); setDeliveryConfigDirty(false); }}>Cancel</button>
                                <button className="settings-btn-save" onClick={saveDeliveryConfig}>Save Changes</button>
                            </>
                        )}
                    </div>
                </div>
                {deliveryConfigMsg && <div className={`settings-msg settings-msg--${deliveryConfigMsg.type}`}>{deliveryConfigMsg.text}</div>}
                <div className="settings-form-grid">
                    <div className="settings-field">
                        <label>Maximum Items Allowed Per Order</label>
                        <input
                            type="number"
                            min="1"
                            value={deliveryConfigDraft.maxItemsPerOrder}
                            onChange={(e) => { setDeliveryConfigDraft({ ...deliveryConfigDraft, maxItemsPerOrder: e.target.value }); setDeliveryConfigDirty(true); }}
                        />
                    </div>
                    <div className="settings-field">
                        <label>4-Wheel Auto-Apply Threshold</label>
                        <input
                            type="number"
                            min="1"
                            value={deliveryConfigDraft.fourWheelItemThreshold}
                            onChange={(e) => { setDeliveryConfigDraft({ ...deliveryConfigDraft, fourWheelItemThreshold: e.target.value }); setDeliveryConfigDirty(true); }}
                        />
                    </div>
                </div>
            </div>

            <div className="settings-card">
                <div className="settings-card-header">
                    <h2 className="settings-card-title">Delivery Methods</h2>
                    <div className="settings-card-actions">
                        {deliveryMethodsDirty && (
                            <>
                                <button className="settings-btn-cancel" onClick={() => { loadSettings(); setDeliveryMethodsDirty(false); }}>Cancel</button>
                                <button className="settings-btn-save" onClick={saveDeliveryMethods}>Save Changes</button>
                            </>
                        )}
                    </div>
                </div>
                {deliveryMethodsMsg && <div className={`settings-msg settings-msg--${deliveryMethodsMsg.type}`}>{deliveryMethodsMsg.text}</div>}
                <div className="ss-methods-list">
                    {deliveryMethods.map((method) => (
                        <DeliveryMethodCard
                            key={method.id}
                            method={method}
                            coverageTypeOptions={coverageTypeOptions}
                            coverageOptionsByType={coverageOptionsByType}
                            onChange={(updated) => {
                                setDeliveryMethods((prev) => prev.map((item) => item.id === updated.id ? updated : item));
                                setDeliveryMethodsDirty(true);
                            }}
                            onDelete={(id) => {
                                setDeliveryMethods((prev) => prev.filter((item) => item.id !== id));
                                setDeliveryMethodsDirty(true);
                            }}
                        />
                    ))}
                </div>
                <button className="ss-btn-add" onClick={() => { setDeliveryMethods((prev) => [...prev, createDeliveryMethod()]); setDeliveryMethodsDirty(true); }} type="button">
                    <PlusIcon />
                    Add Delivery Method
                </button>
            </div>
        </div>
    );
}
